import React, { useState, useEffect, useRef } from 'react';
import { collection, doc, addDoc, onSnapshot, query, orderBy, writeBatch, runTransaction, serverTimestamp, increment } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/authContext';
import { HeartIcon, ChatBubbleBottomCenterTextIcon, PlayIcon, PauseIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { Link } from 'react-router-dom';

// A custom icon for "Prayed"
const PrayIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
    <path d="M18 7.5a1.5 1.5 0 00-3 0v2.065a6.01 6.01 0 00-6.5 5.435V16.5a1.5 1.5 0 003 0v-1.5a1.5 1.5 1.5 0 011.5-1.5h1.5a1.5 1.5 0 011.5 1.5v1.5a1.5 1.5 0 003 0v-2.065a6.01 6.01 0 00-6.5-5.435V7.5z" />
  </svg>
);

const StoryReels = ({ story, autoPlay = false, onSoundEnabled = null, userHasEnabledSound = false }) => {
  const { currentUser, userData } = useAuth();
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [showComments, setShowComments] = useState(false);
  const [userHasLiked, setUserHasLiked] = useState(false);
  const [userHasPrayed, setUserHasPrayed] = useState(false);
  const [likeCount, setLikeCount] = useState(story.likeCount || 0);
  const [prayerCount, setPrayerCount] = useState(story.prayerCount || 0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(!userHasEnabledSound); // Start unmuted if user has enabled sound
  const [hasError, setHasError] = useState(false);
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  const videoRef = useRef(null);

  // Define videoSrc at the top level so it's available everywhere
  const videoSrc = story.playableURL;

  // Helper to get user's display name
  const getUserDisplayName = () => {
    return userData?.name || currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Anonymous';
  };

  // Helper to check if text needs truncation (simplified - we'll use CSS for line clamping)
  const needsTruncation = (text) => {
    if (!text) return false;
    // For now, we'll show the "See more" button for any text longer than 100 characters
    // This is a simple heuristic - in a real app you might want to measure actual rendered height
    return text.length > 100;
  };

  // Auto-play effect when autoPlay prop changes
  useEffect(() => {
    if (autoPlay && videoRef.current && !isLoading && !hasError) {
      const playVideo = async () => {
        try {
          // If user has enabled sound before, start unmuted
          if (userHasEnabledSound) {
            videoRef.current.muted = false;
            setIsMuted(false);
          } else {
            // Ensure video is muted for mobile autoplay
            videoRef.current.muted = true;
            setIsMuted(true);
          }
          
          const playPromise = videoRef.current.play();
          if (playPromise !== undefined) {
            await playPromise;
            console.log('[StoryReels] Auto-play successful');
          }
        } catch (error) {
          console.error('Auto-play failed:', error);
          // If autoplay fails, keep video muted and ready for user interaction
          if (videoRef.current) {
            videoRef.current.muted = true;
            setIsMuted(true);
          }
        }
      };
      
      // Try to play immediately when video is ready
      if (videoRef.current.readyState >= 2) { // HAVE_CURRENT_DATA
        playVideo();
      } else {
        // Wait for video to be ready
        const timer = setTimeout(playVideo, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [autoPlay, isLoading, hasError, userHasEnabledSound]);

  useEffect(() => {
    // A story must have an authorId to fetch interactions
    if (!story.authorId || !story.id) return;

    console.log('[StoryReels] Rendering story:', story);
    if (videoSrc) {
      console.log('[StoryReels] videoSrc to be loaded:', videoSrc);
    }

    const storyRef = doc(db, 'users', story.authorId, 'stories', story.id);

    // Listener for live updates to like and prayer counts on the story document
    const unsubscribeStory = onSnapshot(storyRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setLikeCount(data.likeCount || 0);
        setPrayerCount(data.prayerCount || 0);
      }
    });

    // Listener for comments
    const commentsRef = collection(storyRef, 'comments');
    const q = query(commentsRef, orderBy('createdAt', 'asc'));
    const unsubscribeComments = onSnapshot(q, (snapshot) => {
      setComments(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    // Check if user has liked or prayed
    let unsubscribeLikes, unsubscribePrayers;
    if (currentUser) {
      const likeRef = doc(storyRef, 'likes', currentUser.uid);
      unsubscribeLikes = onSnapshot(likeRef, (doc) => {
        setUserHasLiked(doc.exists());
      });

      const prayerRef = doc(storyRef, 'prayers', currentUser.uid);
      unsubscribePrayers = onSnapshot(prayerRef, (doc) => {
        setUserHasPrayed(doc.exists());
      });
    }

    return () => {
      unsubscribeStory();
      unsubscribeComments();
      if (unsubscribeLikes) unsubscribeLikes();
      if (unsubscribePrayers) unsubscribePrayers();
    };
  }, [story.id, story.authorId, currentUser]);

  // Video event handlers
  const handleVideoLoad = () => {
    console.log('[StoryReels] Video loaded successfully:', videoSrc);
    setIsLoading(false);
    setHasError(false);
  };

  const handleVideoPlay = () => {
    console.log('[StoryReels] Video started playing:', videoSrc);
    setIsPlaying(true);
  };

  const handleVideoPause = () => {
    console.log('[StoryReels] Video paused:', videoSrc);
    setIsPlaying(false);
  };

  const handleVideoError = (error) => {
    console.error('[StoryReels] Video error:', error, 'URL:', videoSrc);
    setIsLoading(false);
    setHasError(true);
  };

  const handleVideoClick = () => {
    if (videoRef.current) {
      try {
        // If video is muted, unmute it
        if (videoRef.current.muted) {
          videoRef.current.muted = false;
          setIsMuted(false);
          console.log('[StoryReels] Video unmuted');
          // Notify parent that user has enabled sound
          if (onSoundEnabled) {
            onSoundEnabled();
          }
        } else {
          // If video is not playing, play it
          if (videoRef.current.paused) {
            videoRef.current.play().catch(error => {
              console.error('Error playing video:', error);
            });
          } else {
            // If video is playing, pause it
            videoRef.current.pause();
          }
        }
      } catch (error) {
        console.error('Error handling video click:', error);
      }
    }
  };



  const handleInteraction = async (type) => {
    if (!currentUser) {
      alert("Please log in to interact.");
      return;
    }
    // Ensure we have the author's ID to locate the story document
    if (!story.authorId || !story.id) {
        console.error("Story author ID or Story ID is missing.");
        return;
    }

    const storyRef = doc(db, 'users', story.authorId, 'stories', story.id);

    if (type === 'like' || type === 'pray') {
      const interactionCollectionName = type === 'like' ? 'likes' : 'prayers';
      const interactionRef = doc(storyRef, interactionCollectionName, currentUser.uid);
      const hasInteracted = type === 'like' ? userHasLiked : userHasPrayed;
      const countUpdate = hasInteracted ? -1 : 1;
      const fieldToUpdate = type === 'like' ? 'likeCount' : 'prayerCount';
      
      const batch = writeBatch(db);

      if (hasInteracted) {
        batch.delete(interactionRef);
      } else {
        batch.set(interactionRef, { 
          userId: currentUser.uid,
          userName: getUserDisplayName(),
          userPhotoURL: currentUser.photoURL,
          createdAt: serverTimestamp()
        });
      }
      
      batch.update(storyRef, { [fieldToUpdate]: increment(countUpdate) });
      
      await batch.commit();
      
    } else if (type === 'comment' && commentText.trim()) {
      const commentsRef = collection(storyRef, 'comments');
      await addDoc(commentsRef, {
        text: commentText,
        userId: currentUser.uid,
        userName: getUserDisplayName(),
        userPhotoURL: currentUser.photoURL,
        createdAt: serverTimestamp(),
      });
      setCommentText('');
    }
  };

  // Use the live state variables which are updated by the onSnapshot listener
  const displayLikes = likeCount;
  const displayPrayers = prayerCount;

  return (
    <div className="relative w-full max-w-sm mx-auto bg-black rounded-lg overflow-hidden" style={{ aspectRatio: '9/16' }}>
      {/* Video Container */}
      <div className="relative w-full h-full">
        {videoSrc && (
          <>
            <video
              ref={videoRef}
              src={videoSrc}
              className="w-full h-full object-cover"
              preload="metadata"
              playsInline
              muted={isMuted}
              autoPlay
              poster={story.posterURL || story.photoURL || undefined}
              onLoadedMetadata={(e) => {
                console.log('[StoryReels] Video metadata loaded');
                // Auto-play the video (muted) when metadata is loaded
                if (e.target.duration > 0) {
                  e.target.currentTime = 0.1;
                  // Try to auto-play muted
                  e.target.play().catch(error => {
                    console.log('[StoryReels] Auto-play failed (expected on mobile):', error);
                  });
                }
                // Hide loading overlay when poster frame is ready
                setIsLoading(false);
              }}
              onSeeked={(e) => {
                console.log('[StoryReels] Video seeked to poster frame');
                // Hide loading overlay when poster frame is ready
                setIsLoading(false);
              }}
              onLoadedData={handleVideoLoad}
              onPlay={handleVideoPlay}
              onPause={handleVideoPause}
              onError={handleVideoError}
              onClick={handleVideoClick}
            >
              Your browser does not support the video tag.
            </video>
            
            {/* Loading Overlay */}
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
              </div>
            )}

            {/* Error Overlay */}
            {hasError && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-75">
                <div className="text-center text-white">
                  <p className="text-sm mb-2">Video could not be loaded</p>
                  <button 
                    onClick={() => {
                      setHasError(false);
                      setIsLoading(true);
                      if (videoRef.current) {
                        videoRef.current.load();
                      }
                    }}
                    className="px-4 py-2 bg-white text-black rounded-lg text-sm hover:bg-gray-100"
                  >
                    Retry
                  </button>
                </div>
              </div>
            )}

            {/* Play/Pause Overlay */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              {!isPlaying && !isLoading && !hasError && (
                <div className="bg-black bg-opacity-50 rounded-full p-4">
                  <PlayIcon className="h-8 w-8 text-white" />
                </div>
              )}
            </div>
          </>
        )}

        {/* Story Content Overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/70 to-transparent p-4">
          {/* Author Info */}
          <div className="flex items-center space-x-3 mb-3">
            <img 
              className="h-10 w-10 rounded-full object-cover border-2 border-white shadow-lg" 
              src={story.authorPhotoURL || `https://ui-avatars.com/api/?name=${story.authorName || 'A'}&background=random`} 
              alt="" 
            />
            <div className="flex-1">
              <div className="text-white font-semibold drop-shadow-lg">
                {story.authorName || 'A member'}
              </div>
              <p className="text-white/90 text-sm drop-shadow-md">
                {story.timestamp ? new Date(story.timestamp.toDate()).toLocaleDateString() : 'Recently'}
              </p>
            </div>
          </div>

          {/* Story Text */}
          {story.text && (
            <div className="mb-4">
              <p 
                className={`text-white text-sm leading-relaxed drop-shadow-lg font-medium ${
                  !isTextExpanded ? 'line-clamp-2' : ''
                }`}
              >
                {story.text}
              </p>
              {needsTruncation(story.text) && (
                <button 
                  onClick={() => setIsTextExpanded(!isTextExpanded)} 
                  className="text-white text-xs underline mt-1 hover:text-gray-200 transition-colors"
                >
                  {isTextExpanded ? 'Show less' : 'See more'}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Right Side Interaction Buttons - Facebook Style */}
        <div className="absolute right-4 top-1/2 transform -translate-y-1/2 flex flex-col items-center space-y-6">
          {/* Likes */}
          <button 
            onClick={() => handleInteraction('like')} 
            className="flex flex-col items-center space-y-1 text-white hover:text-red-400 transition-colors drop-shadow-lg"
          >
            {userHasLiked ? 
              <HeartIconSolid className="h-8 w-8 text-red-500 drop-shadow-md" /> : 
              <HeartIcon className="h-8 w-8 drop-shadow-md" />
            }
            <span className="text-xs font-medium drop-shadow-md">{displayLikes > 0 && displayLikes}</span>
          </button>

          {/* Prayers */}
          <button 
            onClick={() => handleInteraction('pray')} 
            className={`flex flex-col items-center space-y-1 text-white hover:text-blue-400 transition-colors drop-shadow-lg ${userHasPrayed ? 'text-blue-400' : ''}`}
          >
            <PrayIcon />
            <span className="text-xs font-medium drop-shadow-md">{displayPrayers > 0 && displayPrayers}</span>
          </button>

          {/* Comments */}
          <button 
            onClick={() => setShowComments(!showComments)} 
            className="flex flex-col items-center space-y-1 text-white hover:text-gray-300 transition-colors drop-shadow-lg"
          >
            <ChatBubbleBottomCenterTextIcon className="h-8 w-8 drop-shadow-md" />
            <span className="text-xs font-medium drop-shadow-md">{comments.length > 0 && comments.length}</span>
          </button>
        </div>
      </div>

      {/* Comments Section - Slides up from bottom */}
      {showComments && (
        <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-lg max-h-64 overflow-hidden">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800">Comments</h3>
              <button 
                onClick={() => setShowComments(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>
            
            {/* Comment Input */}
            <div className="flex items-start space-x-3 mb-4">
              <img 
                src={currentUser?.photoURL || '/default-avatar.png'} 
                alt="Your avatar" 
                className="h-8 w-8 rounded-full"
              />
              <div className="flex-1">
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Write a comment..."
                  className="w-full p-2 border rounded-md text-sm resize-none"
                  rows="2"
                />
                <button 
                  onClick={() => handleInteraction('comment')} 
                  className="mt-2 px-3 py-1 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600"
                >
                  Post
                </button>
              </div>
            </div>

            {/* Comment List */}
            <div className="space-y-3 max-h-32 overflow-y-auto">
              {comments.map(comment => (
                <div key={comment.id} className="flex items-start space-x-3">
                  <img 
                    src={comment.userPhotoURL || `https://ui-avatars.com/api/?name=${comment.userName || 'A'}&background=random`} 
                    alt={`${comment.userName}'s avatar`} 
                    className="h-6 w-6 rounded-full object-cover"
                  />
                  <div className="flex-1">
                    <p className="text-sm text-gray-800">
                      <span className="font-semibold">{comment.userName}</span> {comment.text}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {comment.createdAt?.toDate().toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoryReels; 